/**
 * BulkActionBar — Sticky-Bar am unteren Bildschirmrand fuer Bulk-Aktionen (ADMIN-03..06).
 *
 * Erscheint nur wenn store.selectedLeadIds.size > 0.
 * Position: fixed bottom-4 centered (left-1/2 -translate-x-1/2) z-40.
 *
 * Hintergrund: #1A1A1A (schwarz) mit weisser Schrift — OK, NIE weisse Schrift auf Gelb.
 * Buttons: #E9FF57 (gelb) mit #1A1A1A (schwarz) Schrift.
 *
 * Buttons:
 *  - "Tag setzen" → BulkTagModal mode='add'
 *  - "Tag entfernen" → BulkTagModal mode='remove'
 *  - "Status wechseln" → BulkStatusModal
 *  - "Bearbeiter zuweisen" → BulkAssignUserModal
 *  - "Abbrechen" → store.clearSelection()
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useLeadBoard } from '../store/useLeadBoard';
import BulkTagModal from './BulkTagModal';
import BulkStatusModal from './BulkStatusModal';
import BulkAssignUserModal from './BulkAssignUserModal';

type ActiveModal = 'tag-add' | 'tag-remove' | 'status' | 'assign' | null;

export default function BulkActionBar() {
  const selectedLeadIds = useLeadBoard((s) => s.selectedLeadIds);
  const clearSelection = useLeadBoard((s) => s.clearSelection);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  // Nur rendern wenn Karten selektiert
  if (selectedLeadIds.size === 0) return null;

  const closeModal = () => setActiveModal(null);

  return (
    <>
      {/* Sticky-Bar */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-3 rounded-lg shadow-xl bg-[#1A1A1A] text-white"
        role="toolbar"
        aria-label="Bulk-Aktionen"
      >
        {/* Karten-Zahl */}
        <span className="text-sm font-medium whitespace-nowrap mr-1">
          {selectedLeadIds.size} {selectedLeadIds.size === 1 ? 'Karte' : 'Karten'} ausgewaehlt
        </span>

        {/* Trennlinie */}
        <span className="w-px h-5 bg-gray-600 mx-1" />

        {/* Tag setzen */}
        <button
          type="button"
          onClick={() => setActiveModal('tag-add')}
          className="px-3 py-1.5 text-xs font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:opacity-90 whitespace-nowrap"
        >
          Tag setzen
        </button>

        {/* Tag entfernen */}
        <button
          type="button"
          onClick={() => setActiveModal('tag-remove')}
          className="px-3 py-1.5 text-xs font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:opacity-90 whitespace-nowrap"
        >
          Tag entfernen
        </button>

        {/* Status wechseln */}
        <button
          type="button"
          onClick={() => setActiveModal('status')}
          className="px-3 py-1.5 text-xs font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:opacity-90 whitespace-nowrap"
        >
          Status wechseln
        </button>

        {/* Bearbeiter zuweisen */}
        <button
          type="button"
          onClick={() => setActiveModal('assign')}
          className="px-3 py-1.5 text-xs font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:opacity-90 whitespace-nowrap"
        >
          Bearbeiter zuweisen
        </button>

        {/* Trennlinie */}
        <span className="w-px h-5 bg-gray-600 mx-1" />

        {/* Abbrechen */}
        <button
          type="button"
          onClick={clearSelection}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded border border-gray-500 text-white hover:bg-gray-700 whitespace-nowrap"
          aria-label="Auswahl aufheben"
        >
          <X className="w-3 h-3" />
          Abbrechen
        </button>
      </div>

      {/* Modale */}
      {activeModal === 'tag-add' && (
        <BulkTagModal mode="add" onClose={closeModal} />
      )}
      {activeModal === 'tag-remove' && (
        <BulkTagModal mode="remove" onClose={closeModal} />
      )}
      {activeModal === 'status' && (
        <BulkStatusModal onClose={closeModal} />
      )}
      {activeModal === 'assign' && (
        <BulkAssignUserModal onClose={closeModal} />
      )}
    </>
  );
}
